/**
 * useAppState
 *
 * Thin React wrappers around Capacitor's cross-platform `appStateChange`
 * lifecycle event. This is the single source of truth for "is the user
 * currently looking at the app?" across all three deploy targets:
 *
 * - Native iOS/Android: backed by the OS foreground/background lifecycle.
 * - Installed PWA and plain browser tab: backed by the Page Visibility API
 *   (`isActive === (document.hidden !== true)`), so it works whether or not
 *   the user has added the app to their home screen.
 *
 * Prefer these hooks over hand-rolled `visibilitychange` listeners so the app
 * has one lifecycle source that behaves consistently on web and native.
 */

import { useEffect, useRef, type MutableRefObject } from 'react';
import { App, type AppState } from '@capacitor/app';

export type AppActiveListener = (isActive: boolean) => void;

/**
 * Invokes `onChange` whenever the app transitions between foreground (active)
 * and background (inactive). The callback is held in a ref so the underlying
 * listener stays registered across renders without re-subscribing.
 */
export function useAppStateChange(onChange: AppActiveListener): void {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const handlePromise = App.addListener(
      'appStateChange',
      ({ isActive }: AppState) => {
        onChangeRef.current(isActive);
      }
    );
    return () => {
      // addListener resolves asynchronously; remove once it does, even if the
      // component already unmounted.
      handlePromise.then(handle => handle.remove());
    };
  }, []);
}

/**
 * Returns a ref tracking whether the app is currently active. Useful for
 * synchronous reads inside intervals or event handlers where awaiting
 * `App.getState()` would be awkward. Seeded from the current visibility state
 * so it is correct on first render (before any transition fires).
 */
export function useAppActiveRef(): MutableRefObject<boolean> {
  const activeRef = useRef(
    typeof document === 'undefined' || document.visibilityState !== 'hidden'
  );
  useAppStateChange(isActive => {
    activeRef.current = isActive;
  });
  return activeRef;
}
